// ============================================
// FILE 1: CustomDataTable.jsx (Updated)
// ============================================
import React, { useState, useMemo, useEffect } from "react";
import {
  Table,
  Input,
  Button,
  Space,
  Select,
  Typography,
  Dropdown,
  Menu,
  Tooltip,
} from "antd";
import {
  SearchOutlined,
  // FilterOutlined,
  EllipsisOutlined,
  FilterOutlined,
  // CloudDownloadOutlined,
} from "@ant-design/icons";
import "./CustomTable.css";
import { CustomTab } from "../Tabs";

const { Title } = Typography;
const { Option } = Select;

/* Custom Pagination Component */
const CustomPagination = ({
  current = 1,
  total = 0,
  pageSize = 10,
  onPageChange,
  onPageSizeChange,
  pageSizeOptions = ["10", "20", "50", "100"],
  showTotal = true,
  selectedCount = 0,
}) => {
  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="flex justify-between items-center px-6 py-4 bg-gray-50 border-t border-gray-200">
      <div className="flex items-center gap-4">
        {showTotal && (
          <span className="text-sm text-gray-600">
            {selectedCount} of {total} row(s) selected.
          </span>
        )}
      </div>
      <div className="flex items-center gap-4">
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">Rows per page:</span>
          <Select
            value={pageSize}
            onChange={(val) => onPageSizeChange && onPageSizeChange(val)}
            size="small"
            className="w-20"
          >
            {pageSizeOptions.map((size) => (
              <Option key={size} value={parseInt(size)}>
                {size}
              </Option>
            ))}
          </Select>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-sm text-gray-600">
            Page {current} of {totalPages}
          </span>
          <div className="flex items-center gap-1">
            <Button
              size="small"
              icon={<span>⟪</span>}
              onClick={() => onPageChange && onPageChange(1)}
              disabled={current === 1}
              className="border-gray-300"
            />
            <Button
              size="small"
              icon={<span>‹</span>}
              onClick={() => onPageChange && onPageChange(current - 1)}
              disabled={current === 1}
              className="border-gray-300"
            />
            <Button
              size="small"
              icon={<span>›</span>}
              onClick={() => onPageChange && onPageChange(current + 1)}
              disabled={current >= totalPages}
              className="border-gray-300"
            />
            <Button
              size="small"
              icon={<span>⟫</span>}
              onClick={() => onPageChange && onPageChange(totalPages)}
              disabled={current >= totalPages}
              className="border-gray-300"
            />
          </div>
        </div>
      </div>
    </div>
  );
};

/* Custom Data Table Component */
const CustomDataTable = ({
  data = [],
  columns = [],
  loading = false,
  title = "Table",
  showTitle = true,
  tabs = null,
  activeTab = null,
  onTabChange = null,
  showSearch = false,
  searchPlaceholder = "Search...",
  searchValue = "",
  onSearchChange = null,
  searchStyle = "client",
  actionButtons = [],
  showFilter = false,
  showCSV = false,
  onFilterClick = null,
  onCSVClick = null,
  rowSelection = null,
  pagination = true,
  pageSize: defaultPageSize = 10,
  pageSizeOptions = ["10", "20", "50", "100"],
  tableClassName = "",
  headerClassName = "",
  selectedRowKeys = [],
  rowActions = null,
  noborder = false,
  // NEW: Server-side pagination props
  paginationMode = "client", // "client" | "server"
  currentPage = 1,
  totalItems = 0,
  onPageChange = null,
  onPageSizeChange = null,
  serverPageSize = null, // Use this when server controls page size

  // NEW: Server-side search props
  searchMode = "client", // "client" | "server"
  onServerSearch = null, // Callback for server-side search
}) => {
  const [searchText, setSearchText] = useState(searchValue);
  const [filteredData, setFilteredData] = useState(data);
  const [clientPage, setClientPage] = useState(1);
  const [clientPageSize, setClientPageSize] = useState(defaultPageSize);

  // Determine which page size to use
  const effectivePageSize =
    paginationMode === "server"
      ? serverPageSize || defaultPageSize
      : clientPageSize;

  useEffect(() => {
    setSearchText(searchValue);
  }, [searchValue]);

  useEffect(() => {
    // For server-side search, don't filter client-side - use data as-is
    if (searchMode === "server") {
      setFilteredData(data);
      return;
    }

    // Only do client-side filtering when searchMode is "client"
    if (searchStyle === "client" && searchText) {
      const filtered = data.filter((item) =>
        Object.values(item).some((val) =>
          String(val).toLowerCase().includes(searchText.toLowerCase())
        )
      );
      setFilteredData(filtered);
    } else {
      setFilteredData(data);
    }
  }, [data, searchText, searchStyle, searchMode]); // Add searchMode to dependencies

  const handleSearch = (value) => {
    const trimmed = value.trim();
    setSearchText(trimmed);

    // ----- Server-side search -----
    if (searchMode === "server" && onServerSearch) {
      if (paginationMode === "server" && onPageChange) {
        onPageChange(1);
      } else {
        setClientPage(1);
      }
      onServerSearch(trimmed);
      return;
    }

    // ----- Client-side search -----
    if (onSearchChange) onSearchChange(trimmed);

    // If trimmed text is empty → reset data & stop filtering
    if (!trimmed) {
      setFilteredData(data);
      return;
    }

    if (searchStyle === "client") {
      const filtered = data.filter((item) =>
        Object.values(item).some((val) =>
          String(val).toLowerCase().includes(trimmed.toLowerCase())
        )
      );
      setFilteredData(filtered);
    }
  };

  // Handle page change based on mode
  const handlePageChange = (page) => {
    if (paginationMode === "server" && onPageChange) {
      onPageChange(page);
    } else {
      setClientPage(page);
    }
  };

  // Handle page size change based on mode
  const handlePageSizeChange = (size) => {
    if (paginationMode === "server" && onPageSizeChange) {
      onPageSizeChange(size);
    } else {
      setClientPageSize(size);
      setClientPage(1);
    }
  };

  const enhancedColumns = useMemo(() => {
    if (!rowActions) return columns;
    return [
      ...columns,
      {
        title: "Action",
        key: "action",
        width: 80,
        fixed: "right",
        render: (_, record) => {
          if (typeof rowActions === "function") return rowActions(record);
          const menu = (
            <Menu>
              {rowActions.map((action, idx) => (
                <Menu.Item
                  key={idx}
                  onClick={() => action.onClick?.(record)}
                  icon={action.icon}
                >
                  {action.label}
                </Menu.Item>
              ))}
            </Menu>
          );
          return (
            <Dropdown
              overlay={menu}
              trigger={["click"]}
              placement="bottomRight"
            >
              <Button type="text" icon={<EllipsisOutlined />} />
            </Dropdown>
          );
        },
      },
    ];
  }, [columns, rowActions]);

  // Get display data based on pagination mode
  const tableData =
    searchMode === "server"
      ? data // For server-side search, use data directly from server
      : searchStyle === "client" && searchText
      ? filteredData
      : data;

  // For client-side pagination, slice the data
  const displayData =
    paginationMode === "client"
      ? tableData.slice(
          (clientPage - 1) * clientPageSize,
          clientPage * clientPageSize
        )
      : tableData; // Server already returns paginated data

const renderActionButtons = () => {
  if (!actionButtons.length) return null;
  return (
    <Space>
      {actionButtons.map((btn, idx) => {
        const buttonElement = (
          <Button
            key={idx}
            type={btn.type || "default"}
            icon={btn.icon}
            onClick={btn.onClick}
            disabled={btn.disabled}
            loading={btn.loading}
            className={btn.className}
            style={btn.style}
          >
            {btn.label}
          </Button>
        );

        // Wrap in Tooltip if tooltip prop exists
        if (btn.tooltip) {
          return (
            <Tooltip key={idx} title={btn.tooltip}>
              <span>{buttonElement}</span>
            </Tooltip>
          );
        }

        return buttonElement;
      })}
    </Space>
  );
};
  const renderTabsContent = () => {
    if (!tabs) return null;
    return (
      <CustomTab
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={onTabChange}
        className="mb-0 rounded-md"
      />
    );
  };

  return (
    <div
      className={`bg-white rounded-lg ${tableClassName}`}
      style={{
        border: noborder ? "none" : "1px solid #e5e7eb",
        boxShadow: noborder ? "none" : "0 1px 2px rgba(0, 0, 0, 0.05)",
      }}
    >
      <div className={`px-6 py-4 ${headerClassName}`}>
        {showTitle && (
          <div className="flex justify-between items-center mb-4">
            <Title level={5} className="mb-0 text-[#1C252E]">
              {title}
            </Title>
            <div className="flex items-center gap-3">
              {renderActionButtons()}
            </div>
          </div>
        )}

        <div className="flex flex-col gap-4 lg:flex-row lg:justify-between lg:items-center">
          <div className="flex">{renderTabsContent()}</div>
          <div className="flex flex-wrap items-center gap-2">
            {showSearch && (
              <Input
                placeholder={searchPlaceholder}
                prefix={<SearchOutlined className="text-gray-400" />}
                value={searchText}
                onChange={(e) => handleSearch(e.target.value)}
                className="w-64 rounded-2xl"
                allowClear
              />
            )}
            {showFilter && (
              <Button
                icon={<FilterOutlined />}
                onClick={onFilterClick}
                className="border-gray-300 text-black text-sm font-semibold rounded-2xl"
              >
                Filter
              </Button>
            )}

            {/*   
            {showCSV && (
              <Button icon={<CloudDownloadOutlined />} onClick={onCSVClick}
                className="border-gray-300 text-black text-sm font-semibold rounded-2xl">CSV</Button>
            )} */}
          </div>
        </div>
      </div>
      <div className="border-t border-gray-200">
        <Table
          key={activeTab}
          columns={enhancedColumns}
          dataSource={displayData}
          loading={loading}
          rowSelection={rowSelection}
          pagination={false}
          className="w-full custom-table"
          size="middle"
          scroll={{ x: true }}
          bordered={false}
        />
        {pagination && (
          <CustomPagination
            current={paginationMode === "server" ? currentPage : clientPage}
            total={paginationMode === "server" ? totalItems : tableData.length}
            pageSize={effectivePageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
            pageSizeOptions={pageSizeOptions}
            selectedCount={selectedRowKeys?.length || 0}
          />
        )}
      </div>
    </div>
  );
};

export { CustomDataTable, CustomPagination };
